import { IconBattery, IconCloudRain, IconTemperature } from "@tabler/icons-react"
import { Card, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { AccelerometerCard } from "./accelerometer-card"
import { formatBatteryVoltage } from "@/lib/ble/parsers"
import type { SensorReading } from "@/types/device"

interface SensorsPanelProps {
  reading: SensorReading
}

export function SensorsPanel({ reading }: SensorsPanelProps) {
  return (
    <div className="space-y-6">
      {/* Accelerometer Section */}
      <div className="space-y-4">
        <div>
          <h3 className="font-medium">Accelerometer</h3>
          <p className="text-sm text-muted-foreground">
            Raw accelerometer readings (X, Y, Z)
          </p>
        </div>
        <AccelerometerCard data={reading.accelerometer} />
      </div>

      {/* PTH Section */}
      <div className="space-y-4">
        <div>
          <h3 className="font-medium">Environmental Sensors</h3>
          <p className="text-sm text-muted-foreground">
            Pressure, temperature, and humidity
          </p>
        </div>
        <div className="grid gap-4 sm:grid-cols-3">
          <Card>
            <CardHeader className="pb-2">
              <div className="flex items-center gap-2">
                <IconTemperature className="size-4 text-red-500" />
                <CardDescription>Sensor Temp</CardDescription>
              </div>
              <CardTitle className="text-2xl tabular-nums">
                {reading.pth
                  ? `${reading.pth.temp.toFixed(1)}°C`
                  : "--"}
              </CardTitle>
            </CardHeader>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <div className="flex items-center gap-2">
                <IconCloudRain className="size-4 text-blue-500" />
                <CardDescription>Pressure</CardDescription>
              </div>
              <CardTitle className="text-2xl tabular-nums">
                {reading.pth
                  ? `${reading.pth.pressure.toFixed(1)} hPa`
                  : "--"}
              </CardTitle>
            </CardHeader>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <div className="flex items-center gap-2">
                <IconCloudRain className="size-4 text-cyan-500" />
                <CardDescription>Humidity</CardDescription>
              </div>
              <CardTitle className="text-2xl tabular-nums">
                {reading.pth
                  ? `${reading.pth.humidity.toFixed(1)}%`
                  : "--"}
              </CardTitle>
            </CardHeader>
          </Card>
        </div>
      </div>

      {/* Battery Section */}
      <div className="space-y-4">
        <div>
          <h3 className="font-medium">Battery</h3>
          <p className="text-sm text-muted-foreground">
            Device battery voltage
          </p>
        </div>
        <Card className="sm:max-w-xs">
          <CardHeader className="pb-2">
            <div className="flex items-center gap-2">
              <IconBattery className="size-4 text-green-500" />
              <CardDescription>Battery Voltage</CardDescription>
            </div>
            <CardTitle className="text-2xl tabular-nums">
              {formatBatteryVoltage(reading.batteryVoltage)}
            </CardTitle>
          </CardHeader>
        </Card>
      </div>
    </div>
  )
}
